/**
 * Runtime Configuration Utility
 *
 * This utility reads configuration values from multiple sources in order of priority:
 * 1. Runtime config injected into HTML (highest priority)
 * 2. Build-time environment variables (fallback)
 *
 * This allows for flexible configuration that can be overridden at container startup
 * without requiring a rebuild of the application.
 */

interface RuntimeConfigMap {
  [key: string]: string | undefined;
}

let runtimeConfigCache: RuntimeConfigMap | null = null;

/** Global set by canvas-screenshot Lambda via Puppeteer `evaluateOnNewDocument` before navigation. */
export const ZENSEI_CANVAS_CAPTURE_API_BASE_URL_GLOBAL =
  "__ZENSEI_CANVAS_CAPTURE_API_BASE_URL__" as const;

/**
 * Reads the Lambda-injected API origin, which points the capture page at the
 * backend that owns the canvas rather than the one the shell was built with.
 * Unset in every other context.
 */
function getInjectedCanvasCaptureAPIBaseURL(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const value = (window as unknown as Record<string, string | undefined>)[
    ZENSEI_CANVAS_CAPTURE_API_BASE_URL_GLOBAL
  ]?.trim();
  return value || undefined;
}

/**
 * Gets runtime configuration injected into the HTML by the startup script
 * @returns Object containing runtime configuration values
 */
function getRuntimeConfig(): RuntimeConfigMap {
  if (runtimeConfigCache !== null) {
    return runtimeConfigCache;
  }

  let runtimeConfig: RuntimeConfigMap = {};

  try {
    // Try to read the runtime config script tag injected by the startup script
    const runtimeConfigElement = document.getElementById("runtime-config");

    if (runtimeConfigElement && runtimeConfigElement.textContent) {
      runtimeConfig = JSON.parse(runtimeConfigElement.textContent);
      console.log(
        "🚀 Loaded runtime configuration:",
        Object.keys(runtimeConfig).length + " values",
      );
    }
  } catch (error) {
    console.warn("⚠️ Failed to parse runtime configuration:", error);
  }

  // Merged here rather than in a bootstrap: `config` is a frozen singleton
  // built on first import, so anything later than this read is too late.
  const captureAPIBaseURL = getInjectedCanvasCaptureAPIBaseURL();
  if (captureAPIBaseURL) {
    runtimeConfig.VITE_API_BASE_URL = captureAPIBaseURL;
  }

  runtimeConfigCache = runtimeConfig;
  return runtimeConfigCache;
}

/**
 * Gets a configuration value with runtime override support
 * @param key The environment variable key (e.g., 'VITE_API_BASE_URL')
 * @param fallback Optional fallback value if neither runtime nor build-time value exists
 * @returns The configuration value, preferring runtime over build-time
 */
export function getRuntimeEnvVar(
  key: string,
  fallback?: string,
): string | undefined {
  const runtimeConfig = getRuntimeConfig();

  // First check runtime config (injected at container startup)
  const runtimeValue = runtimeConfig[key];
  if (runtimeValue && runtimeValue.trim() !== "") {
    return runtimeValue;
  }

  // Fallback to build-time environment variable
  const buildTimeValue = import.meta.env[key];
  if (buildTimeValue && buildTimeValue.trim() !== "") {
    return buildTimeValue;
  }

  // Final fallback
  return fallback;
}

/**
 * Gets a boolean configuration value with runtime override support
 * @param key The environment variable key
 * @param fallback Optional fallback value
 * @returns Boolean value, where 'true' string becomes true boolean
 */
export function getRuntimeEnvVarBoolean(
  key: string,
  fallback: boolean = false,
): boolean {
  const value = getRuntimeEnvVar(key);
  if (value === undefined) {
    return fallback;
  }
  return value.toLowerCase() === "true";
}

/**
 * Logs the current configuration state for debugging
 */
export function logConfigurationState(): void {
  const runtimeConfig = getRuntimeConfig();
  const hasRuntimeConfig = Object.keys(runtimeConfig).length > 0;

  console.group("🔧 Configuration State");
  console.log("Runtime config available:", hasRuntimeConfig);

  if (hasRuntimeConfig) {
    console.log("Runtime config keys:", Object.keys(runtimeConfig));

    // Log a few key values for debugging (without sensitive data)
    const debugKeys = [
      "VITE_APP_ENV",
      "VITE_API_BASE_URL",
      "VITE_APP_BASE_URL",
      "VITE_MARKETING_BASE_URL",
    ];
    debugKeys.forEach((key) => {
      const value = runtimeConfig[key];
      if (value) {
        console.log(`${key}:`, value);
      }
    });
  } else {
    console.log("Using build-time configuration only");
  }

  console.groupEnd();
}
